import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';

import { ApiUrl } from '../shared/models/apiurl.models';
import { SystemApp } from '../constans/SystemConfigurations';
import { UserStorageService } from './UserStorageService';
import { TokenStore } from './token-store';
import { ProfileStateService } from '../pages/personal/profile-state.service';
import { withoutGlobalSpinner } from '../theme/utils/spinner-context';

/**
 * 'unknown' = not resolved yet, or the lookup failed. The UI shows NO badge
 * for it rather than guessing, so an API hiccup never mislabels a VIP as
 * regular (or the other way round).
 */
export type MembershipStatus = 'vip' | 'regular' | 'unknown';

export interface AccountSummary {
  firstName: string;
  lastName: string;
  /** "first last", or '' when neither is known. */
  fullName: string;
  email: string;
  /** A usable image URL, or null -> show initials. */
  avatarUrl: string | null;
  /** Up to two letters, '' when there is nothing to build them from. */
  initials: string;
  membership: MembershipStatus;
  /** True while /user/user_info/ is in flight. */
  loading: boolean;
}

const EMPTY: AccountSummary = {
  firstName: '', lastName: '', fullName: '', email: '',
  avatarUrl: null, initials: '', membership: 'unknown', loading: false,
};

/**
 * Display state for the signed-in user's account chip in the header
 * (avatar, name, email, VIP / regular).
 *
 * Source of truth is GET /user/user_info/ - ONE call per session / login:
 * - `is_vip_active` is the server's `core.vip.is_vip_now()` (is_vip AND
 *   vip_until in the future). The raw `is_vip` flag is deliberately ignored:
 *   it stays true after a subscription lapses. Every VIP tier (Go / Plus /
 *   Pro) is a VIP subscription; per-ad paid routes (LEVEL2/3) are not a
 *   membership and never make a user VIP.
 * - `avatar` is "" when the user has no picture (the login response turns
 *   that "" into the login endpoint's own URL, so the stored copy can be
 *   bogus - see normalizeAvatar()).
 *
 * The values cached in localStorage at login (first_name, last_name, email,
 * avatar) are shown immediately so the header doesn't flash empty, then
 * replaced by the server's answer.
 */
@Injectable({ providedIn: 'root' })
export class AccountSummaryService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = inject(ApiUrl);
  private readonly userStorage = inject(UserStorageService);
  private readonly profileState = inject(ProfileStateService);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  private readonly stateSubject = new BehaviorSubject<AccountSummary>(EMPTY);
  readonly state$: Observable<AccountSummary> = this.stateSubject.pipe(distinctUntilChanged());

  /** Whether the server answer for the CURRENT session has been applied. */
  private loaded = false;
  private request: Subscription | null = null;

  constructor() {
    if (!this.isBrowser) {
      return;
    }
    this.seedFromStorage();

    // Login / account switch: drop the previous account at once, then load.
    this.userStorage.loggedIn$.subscribe(() => {
      this.loaded = false;
      this.seedFromStorage();
      this.refresh();
    });

    // Profile page saved a new name or picture - reflect it without a reload.
    this.profileState.updated$.subscribe(user => this.applyProfile(user));
  }

  get snapshot(): AccountSummary {
    return this.stateSubject.value;
  }

  /** Load once per session. Safe to call from every component init. */
  ensureLoaded(): void {
    if (!this.isBrowser || this.loaded || this.request || !TokenStore.isLoggedIn()) {
      return;
    }
    this.refresh();
  }

  /** Re-read the account from the server. */
  refresh(): void {
    if (!this.isBrowser) {
      return;
    }
    this.request?.unsubscribe();
    this.patch({ loading: true });

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: this.apiUrl.getJWTToken(),
    });
    this.request = this.http
      .get<any>(this.apiUrl.getUserInfo(), { headers, context: withoutGlobalSpinner() })
      .subscribe({
        next: res => {
          this.request = null;
          this.loaded = true;
          this.applyServer(res);
        },
        error: () => {
          // Keep what we already show (cached name / picture) and hide the
          // badge. `loaded` stays false so the next header init retries.
          this.request = null;
          this.patch({ loading: false, membership: 'unknown' });
        },
      });
  }

  /** Logout: forget everything about the previous account. */
  clear(): void {
    this.request?.unsubscribe();
    this.request = null;
    this.loaded = false;
    this.stateSubject.next(EMPTY);
  }

  // ── internals ────────────────────────────────────────────────────────────

  private seedFromStorage(): void {
    const u = this.userStorage.getUser();
    this.stateSubject.next(this.build({
      first_name: u.first_name,
      last_name: u.last_name,
      email: u.email,
      avatar: u.avatar,
    }, 'unknown', false));
  }

  private applyServer(res: any): void {
    if (!res || typeof res !== 'object') {
      this.patch({ loading: false, membership: 'unknown' });
      return;
    }
    // Only the resolved field decides VIP. An older backend without it ->
    // 'unknown' (no badge) instead of trusting the raw is_vip flag.
    const membership: MembershipStatus =
      res.is_vip_active === true ? 'vip' : res.is_vip_active === false ? 'regular' : 'unknown';
    const next = this.build(res, membership, false);
    this.stateSubject.next(next);
    this.persist(next);
  }

  /** ProfileStateService payload (info.component userInfo). */
  private applyProfile(user: any): void {
    if (!user || typeof user !== 'object') {
      return;
    }
    const cur = this.snapshot;
    const next = this.build({
      first_name: user.first_name ?? cur.firstName,
      last_name: user.last_name ?? cur.lastName,
      email: user.email ?? cur.email,
      avatar: user.avatar !== undefined ? user.avatar : cur.avatarUrl,
    }, cur.membership, cur.loading);
    this.stateSubject.next(next);
    this.persist(next);
  }

  private build(src: any, membership: MembershipStatus, loading: boolean): AccountSummary {
    const firstName = AccountSummaryService.clean(src.first_name);
    const lastName = AccountSummaryService.clean(src.last_name);
    const email = AccountSummaryService.clean(src.email);
    return {
      firstName,
      lastName,
      fullName: `${firstName} ${lastName}`.trim(),
      email,
      avatarUrl: AccountSummaryService.normalizeAvatar(src.avatar),
      initials: AccountSummaryService.initialsOf(firstName, lastName, email),
      membership,
      loading,
    };
  }

  private patch(p: Partial<AccountSummary>): void {
    this.stateSubject.next({ ...this.snapshot, ...p });
  }

  /** Keep the login-time cache in line, so the next page load starts right. */
  private persist(s: AccountSummary): void {
    try {
      const set = (k: string, v: string | null) => v ? localStorage.setItem(k, v) : localStorage.removeItem(k);
      set('first_name', s.firstName);
      set('last_name', s.lastName);
      set('email', s.email);
      // Never persist a data: URL (an unsaved crop) - it can be megabytes.
      set('avatar', s.avatarUrl && !s.avatarUrl.startsWith('data:') ? s.avatarUrl : null);
    } catch (e) {
      // storage disabled - display state is still correct
    }
  }

  private static clean(v: any): string {
    if (v === null || v === undefined) return '';
    const s = String(v).trim();
    return s === 'null' || s === 'undefined' ? '' : s;
  }

  static initialsOf(first: string, last: string, email: string): string {
    const a = first.charAt(0);
    const b = last.charAt(0);
    if (a || b) return (a + b).toUpperCase();
    return email ? email.charAt(0).toUpperCase() : '';
  }

  /**
   * A URL that can plausibly be shown as an image, or null.
   * - "", "null", "undefined" -> null
   * - data:image/... -> as is (fresh crop in the profile page)
   * - "/media/x.png" -> prefixed with the API host
   * - an http(s) URL whose path ends with "/" -> null: that is an API
   *   endpoint, which is what the login response returns for a user with
   *   no picture (build_absolute_uri("")). Real files never end in "/".
   * Anything that still fails to load is handled by the <img> error fallback.
   */
  static normalizeAvatar(raw: any): string | null {
    const v = AccountSummaryService.clean(raw);
    if (!v) return null;
    if (v.startsWith('data:image/')) return v;
    const url = v.startsWith('/') && !v.startsWith('//')
      ? SystemApp.base_url_api.replace(/\/+$/, '') + v
      : v;
    if (!/^https?:\/\//i.test(url)) return null;
    try {
      const path = new URL(url).pathname;
      if (!path || path.endsWith('/')) return null;
    } catch (e) {
      return null;
    }
    return url;
  }
}
